// Offline Sync Service - uploads pending recordings directly to Azure, then notifies backend
import axios from 'axios';
import {
  getOfflineQueue,
  removeFromOfflineQueue,
  getPendingRecordings,
  markRecordingSynced,
  deletePendingRecording
} from './offlineDB';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get auth token from localStorage
const getAuthToken = () => {
  try {
    return localStorage.getItem('snd_vms_token');
  } catch (e) {
    return null;
  }
};

// Create axios instance with auth
const createAuthAxios = () => {
  const token = getAuthToken();
  return axios.create({
    baseURL: API_URL,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
};

/**
 * Upload a video Blob directly to Azure using a SAS URL.
 * Uses XMLHttpRequest so we can track upload progress.
 */
const uploadBlobToAzure = (sasUrl, blob, onProgress) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', sasUrl);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('Content-Type', 'video/webm');

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Azure upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    };
    xhr.onerror = () => reject(new Error('Azure upload network error'));
    xhr.send(blob);
  });
};

// Sync all offline queue items (API requests queued while offline)
export const syncOfflineQueue = async (onProgress) => {
  const queue = await getOfflineQueue();

  if (queue.length === 0) {
    console.log('[Sync] No items in offline queue');
    return { success: true, synced: 0, failed: 0 };
  }

  console.log(`[Sync] Syncing ${queue.length} offline queue items...`);

  let synced = 0;
  let failed = 0;
  const api = createAuthAxios();

  for (const item of queue) {
    try {
      switch (item.type) {
        case 'UPDATE_SETTINGS':
          await api.put('/api/settings', item.data);
          break;
        default:
          console.log(`[Sync] Unknown queue item type: ${item.type}`);
      }

      await removeFromOfflineQueue(item.id);
      synced++;

      if (onProgress) onProgress({ current: synced + failed, total: queue.length, item });
    } catch (error) {
      console.error(`[Sync] Failed to sync item ${item.id}:`, error);
      failed++;

      if (error.response && error.response.status >= 400 && error.response.status < 500) {
        await removeFromOfflineQueue(item.id);
      }
    }
  }

  console.log(`[Sync] Queue sync complete. Synced: ${synced}, Failed: ${failed}`);
  return { success: failed === 0, synced, failed };
};

// Shared lock for every pending-recording sync (this service, PackingStation,
// ConnectionStatus). Without it, several 'online' listeners upload the same
// recording at once and the backend receives duplicate confirm-upload calls.
let activeRecordingSync = null;

/**
 * Run fn unless a recording sync is already in progress. If one is, wait for
 * it to finish and return busyResult instead of starting a second sync.
 */
export const withRecordingSyncLock = async (fn, busyResult) => {
  if (activeRecordingSync) {
    await activeRecordingSync.catch(() => {});
    return busyResult;
  }
  activeRecordingSync = Promise.resolve().then(fn);
  try {
    return await activeRecordingSync;
  } finally {
    activeRecordingSync = null;
  }
};

/**
 * Sync pending recordings:
 * 1. Get a SAS upload URL from backend
 * 2. Upload video blob directly to Azure
 * 3. Confirm with backend (saves metadata to MongoDB)
 */
export const syncPendingRecordings = (onProgress) =>
  withRecordingSyncLock(
    () => syncPendingRecordingsUnlocked(onProgress),
    { success: true, synced: 0, failed: 0, skipped: true }
  );

const syncPendingRecordingsUnlocked = async (onProgress) => {
  const recordings = await getPendingRecordings();

  if (recordings.length === 0) {
    console.log('[Sync] No pending recordings to sync');
    return { success: true, synced: 0, failed: 0 };
  }

  console.log(`[Sync] Syncing ${recordings.length} pending recordings...`);

  let synced = 0;
  let failed = 0;
  const api = createAuthAxios();

  for (const recording of recordings) {
    try {
      // 1. Get a SAS URL for direct Azure upload
      const { data: { sasUrl, blobName, cloudUrl } } = await api.get(
        `/api/recordings/upload-url?trackingId=${encodeURIComponent(recording.trackingId)}`
      );

      // 2. Upload blob directly to Azure
      const blob = new Blob([recording.videoBlob], { type: 'video/webm' });
      await uploadBlobToAzure(sasUrl, blob, (pct) => {
        console.log(`[Sync] Uploading ${recording.trackingId}: ${pct}%`);
      });

      // 3. Notify backend — saves metadata to MongoDB
      await api.post('/api/recordings/confirm-upload', {
        trackingId: recording.trackingId,
        blobName,
        cloudUrl,
        duration: recording.duration,
        startTime: recording.startTime,
        fileSize: blob.size,
      });

      await markRecordingSynced(recording.id);
      setTimeout(() => deletePendingRecording(recording.id), 5000);

      synced++;
      if (onProgress) onProgress({ current: synced + failed, total: recordings.length, recording });

      console.log(`[Sync] Synced recording: ${recording.trackingId}`);
    } catch (error) {
      console.error(`[Sync] Failed to sync recording ${recording.id}:`, error);
      failed++;

      // Permanent failure (400 bad request, 409 already on server) — skip to avoid infinite retry
      const status = error.response?.status;
      if (status === 400 || status === 409) {
        await markRecordingSynced(recording.id);
      }
    }
  }

  console.log(`[Sync] Recording sync complete. Synced: ${synced}, Failed: ${failed}`);
  return { success: failed === 0, synced, failed };
};

// Full sync — queue items + pending recordings
export const fullSync = async (onProgress) => {
  console.log('[Sync] Starting full sync...');

  const queueResult = await syncOfflineQueue((progress) => {
    if (onProgress) onProgress({ phase: 'queue', ...progress });
  });

  const recordingsResult = await syncPendingRecordings((progress) => {
    if (onProgress) onProgress({ phase: 'recordings', ...progress });
  });

  return {
    success: queueResult.success && recordingsResult.success,
    queue: queueResult,
    recordings: recordingsResult
  };
};

// Request background sync (if browser supports it)
export const requestBackgroundSync = async () => {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.sync.register('sync-recordings');
      console.log('[Sync] Background sync registered');
      return true;
    } catch (error) {
      console.error('[Sync] Background sync registration failed:', error);
      return false;
    }
  }
  return false;
};

export const canSync = () => navigator.onLine;

// Auto-sync when coming back online
let isSyncing = false;

export const setupAutoSync = () => {
  window.addEventListener('online', async () => {
    if (isSyncing) return;

    isSyncing = true;
    console.log('[Sync] Back online, starting auto-sync...');

    try {
      await fullSync();
    } catch (error) {
      console.error('[Sync] Auto-sync failed:', error);
    } finally {
      isSyncing = false;
    }
  });

  window.addEventListener('sync-offline-data', async () => {
    if (isSyncing) return;

    isSyncing = true;
    try {
      await fullSync();
    } catch (error) {
      console.error('[Sync] Triggered sync failed:', error);
    } finally {
      isSyncing = false;
    }
  });
};

// Initialize auto-sync on module load
setupAutoSync();

export default {
  syncOfflineQueue,
  syncPendingRecordings,
  withRecordingSyncLock,
  fullSync,
  requestBackgroundSync,
  canSync,
  setupAutoSync
};
